/*!
 * Surgomed Variant Utilities
 *
 * Pure helper functions used by surgomed-variant-picker and
 * surgomed-variant-gallery snippets. No DOM access, no side effects.
 *
 * Why a shared file:
 *   Both snippets need to look up variants by option combinations.
 *   Keeping these functions in one place prevents drift and reduces
 *   per-page payload (browser caches this file once).
 *
 * Data shape expected:
 *   variants: Array<{
 *     id: number,
 *     options: [string|null, string|null, string|null],
 *     available: boolean,
 *     price: number
 *   }>
 *
 *   selected: Array<string|null>  // length matches optionCount
 *
 * Browser support: ES5. No arrow functions, no template strings.
 */

(function (root) {
  'use strict';

  /**
   * Find the first variant whose options exactly match every non-null
   * value in `opts`. All option positions must be set (no nulls in opts)
   * for a match to be returned.
   */
  function findVariant(variants, opts) {
    if (!variants || !opts || !opts.length) return null;
    for (var k = 0; k < opts.length; k++) if (opts[k] === null) return null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var match = true;
      for (var j = 0; j < opts.length; j++) {
        if (v.options[j] !== opts[j]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  /**
   * Find variant id by partial option match. Used when only some options
   * are selected (returns the first variant matching all set options).
   * Returns null if no match.
   */
  function findVariantIdByOptions(variants, opts) {
    if (!variants || !opts || !opts.length) return null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var match = true;
      for (var j = 0; j < opts.length; j++) {
        if (opts[j] && v.options[j] !== opts[j]) { match = false; break; }
      }
      if (match) return v.id;
    }
    return null;
  }

  /**
   * Is a given option value reachable given the current selected state?
   * A value is reachable if at least one variant exists that has this
   * value for the given option index AND matches all OTHER currently-
   * selected option values. Null in selected[] means "any value OK".
   * Null in v.options[] is treated as "any value OK" (defensive).
   */
  function isValueReachable(variants, optionCount, optionIndex, value, selected) {
    if (!variants) return false;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.options[optionIndex] !== value) continue;
      var match = true;
      for (var j = 0; j < optionCount; j++) {
        if (j === optionIndex) continue;
        if (selected[j] === null) continue;
        if (v.options[j] === null) continue;
        if (v.options[j] !== selected[j]) { match = false; break; }
      }
      if (match) return true;
    }
    return false;
  }

  /**
   * After picking `value` for `optionIndex`, return a new `selected`
   * array with conflicting selections cleared. A selection is in
   * conflict when no variant has that combination of (this option +
   * other selected options).
   */
  function clearConflictingSelections(variants, optionCount, optionIndex, value, selected) {
    var next = selected.slice();
    next[optionIndex] = value;
    for (var j = 0; j < optionCount; j++) {
      if (j === optionIndex) continue;
      if (next[j] === null) continue;
      var stillValid = false;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v.options[optionIndex] !== value) continue;
        if (v.options[j] !== next[j]) continue;
        stillValid = true;
        break;
      }
      if (!stillValid) next[j] = null;
    }
    return next;
  }

  root.SurgomedVariantUtils = {
    findVariant: findVariant,
    findVariantIdByOptions: findVariantIdByOptions,
    isValueReachable: isValueReachable,
    clearConflictingSelections: clearConflictingSelections
  };
}(window));
